import { type ReactNode, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  type ApiRedirectionTokenData,
  registerRedirectionTokensQueries,
} from '@repo/api-kit/queries/register-redirection-tokens';
import { registerPartnersQueries } from '@repo/api-kit/queries/register-partners';
import { isResponseError } from '@repo/api-kit/queries/utils';
import { useNavigate } from '@repo/poly-hooks/routing';
import { useSegment } from '@repo/poly-hooks/segment';
import { useSession } from '@repo/poly-hooks/session';
import { useZendeskLocalization } from '@repo/poly-hooks/zendesk-localization';
import { isLocaleCode, useI18n } from '@repo/runtime-kit/i18n/i18n-provider';
import {
  isZendeskMounted,
  loadAndAuthenticateZendesk,
  unmountZendesk,
} from '@repo/shared-kit/zendesk/widget';
import { isHashtableItadvisorsPartner } from '@repo/shared-kit/utils/is-hashtable-itadvisors-partner';
import { normalizeZendeskCountry } from '@repo/shared-config/constants/zendesk';
import { useConfig } from '@repo/shared-kit/config/config-provider';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';

export interface PartnerRedirectionOutletContext {
  token: ApiRedirectionTokenData;
}

export function getPartnerRedirectionRedirect(
  token: ApiRedirectionTokenData,
  isAuthenticated: boolean
): string | undefined {
  if (token.flowType === 'not_eligible') return `/${token.id}/not-eligible`;
  if (token.flowUserType === 'other') return `/${token.id}/not-owner`;
  if (token.partnerName?.toLowerCase() === 'mollie' && !isAuthenticated) {
    return `/${token.id}/email-confirmation`;
  }
  if (
    ['norman', 'thomas charlier - beglaubigt.de'].includes(
      token.partnerName?.toLowerCase() ?? ''
    ) ||
    isHashtableItadvisorsPartner(token.partnerName)
  ) {
    if (!isAuthenticated) return `/${token.id}/email-confirmation`;
    return token.registrationId ? 'external-register' : `/${token.id}/not-owner`;
  }
  if (token.companyCreationFromScratch) {
    return isAuthenticated && token.registrationId
      ? `/registrations/${token.registrationId}/start`
      : `/${token.id}/email-confirmation`;
  }
  return undefined;
}

// Ember tracks entry only after the ineligible, non-owner and logged-out Mollie redirects return.
export function shouldTrackPartnerRegisterStarted(
  token: ApiRedirectionTokenData,
  isAuthenticated: boolean
): boolean {
  if (token.flowType === 'not_eligible' || token.flowUserType === 'other') return false;
  if (token.partnerName?.toLowerCase() === 'mollie' && !isAuthenticated) return false;
  return (
    isNormanOrBeglaubigt(token.partnerName) ||
    (Boolean(token.companyCreationFromScratch) && !isHashtableItadvisorsPartner(token.partnerName))
  );
}

function isNormanOrBeglaubigt(partnerName?: string): boolean {
  return ['norman', 'thomas charlier - beglaubigt.de'].includes(partnerName?.toLowerCase() ?? '');
}

export function PartnerRedirectionRoute(): ReactNode {
  const { urls } = useConfig();
  const navigate = useNavigate();
  const segment = useSegment();
  const { isAuthenticated } = useSession();
  const { locale, setLocale } = useI18n();
  const zendeskLocalization = useZendeskLocalization();
  const { pathname } = useLocation();
  const { redirectionId } = useParams<{ redirectionId: string }>();
  const {
    data: token,
    error,
    isError,
    isPending,
  } = useQuery(registerRedirectionTokensQueries.detail(redirectionId));
  const redirect = token && getPartnerRedirectionRedirect(token, isAuthenticated);
  const isExternalRegister = redirect === 'external-register';
  const { error: partnerRegistrationError, isSuccess: hasPartnerRegistration } = useQuery({
    ...registerPartnersQueries.partnerRegistration(token?.registrationId ?? ''),
    enabled: isExternalRegister,
    retry: false,
  });
  const isPartnerRegistrationMissing =
    isResponseError(partnerRegistrationError) && partnerRegistrationError.status === 404;
  const trackedTokenId = useRef<string | null>(null);
  const redirectTarget =
    isPartnerRegistrationMissing && token ? `/${token.id}/not-owner` : redirect;
  const isOnRedirectTarget =
    redirectTarget !== undefined && pathname.replace(/\/$/, '').endsWith(redirectTarget);

  useEffect(() => {
    if (!token) return;
    segment.identify({
      email: token.ownerEmail,
      firstName: token.ownerFirstName,
      partnerName: token.companyCreationFromScratch ? null : token.partnerName,
      organizationName: token.organizationName,
      organizationId: token.organizationId,
      is_company_creation_from_scratch: token.companyCreationFromScratch,
    });
    if (!isAuthenticated && token.locale && isLocaleCode(token.locale)) {
      sessionStorage.setItem('logged-out-locale-code', token.locale);
      setLocale(token.locale);
    }

    if (
      trackedTokenId.current !== token.id &&
      shouldTrackPartnerRegisterStarted(token, isAuthenticated)
    ) {
      trackedTokenId.current = token.id;
      segment.track('onboarding_partner_register_started', {
        country_selected: token.organizationLegalCountry,
        situation: 'company_creation',
        under_registration: true,
        organization_id: token.organizationId,
        partner_name: isNormanOrBeglaubigt(token.partnerName) ? token.partnerName : null,
        is_company_creation_from_scratch: true,
      });
    }

    if (redirect && redirect !== 'external-register' && !isOnRedirectTarget) {
      navigate(redirect, { replace: true });
    } else if (!redirect && isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, isOnRedirectTarget, navigate, redirect, segment, setLocale, token]);

  useEffect(() => {
    if (!isExternalRegister || !token?.registrationId) return;
    if (hasPartnerRegistration) {
      const url = new URL(urls.registerJsURL);
      url.searchParams.set('skip', 'true');
      url.searchParams.set('partner_registration_id', token.registrationId);
      window.location.replace(url);
    } else if (isPartnerRegistrationMissing && !isOnRedirectTarget) {
      navigate(`/${token.id}/not-owner`, { replace: true });
    }
  }, [
    hasPartnerRegistration,
    isExternalRegister,
    isOnRedirectTarget,
    isPartnerRegistrationMissing,
    navigate,
    token,
    urls.registerJsURL,
  ]);

  useEffect(() => {
    if (!token) return;
    const country = normalizeZendeskCountry(token.organizationLegalCountry);
    const isWidgetCurrent =
      isZendeskMounted() && zendeskLocalization.organizationCountry === country;
    zendeskLocalization.organizationCountry = country;
    if (!isWidgetCurrent) {
      unmountZendesk();
      void loadAndAuthenticateZendesk({ orgCountry: country, userLocale: locale });
    }
  }, [locale, token, zendeskLocalization]);

  if (isPending) return null;
  if (isError) {
    if (isResponseError(error) && error.status === 404) {
      return <Navigate replace to="/404" />;
    }
    throw error;
  }
  if (partnerRegistrationError && !isPartnerRegistrationMissing) throw partnerRegistrationError;
  // Ember redirects before its session check, so signed-in users still see their result page.
  if ((redirectTarget && !isOnRedirectTarget) || (!redirectTarget && isAuthenticated)) {
    return null;
  }

  return <Outlet context={{ token } satisfies PartnerRedirectionOutletContext} />;
}
